import { AGSAlternateAnimateBehaviour } from "ags/behaviours/ags-alternate-animate-behaviour";
import AGSAnimateBehaviour from "ags/behaviours/ags-animate-behaviour";
import bigWinCommand from "ags/commands/bigwin-command";
import { SparkResult } from "ags/integration/model/results/spark-result";
import winMagnitude, { BigWinThresholds } from "ags/utils/win-magnitude";
import DimSymbolsCommand, { DimType } from "slotworks/commands/symbols/dim-symbols-command";
import Components from "slotworks/components/components";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import AbstractMediator from "slotworks/mediators/abstract-mediator";
import AutoplayModel from "slotworks/model/autoplay/autoplay-model";
import betModel from "slotworks/model/bets/bet-model";
import GameState from "slotworks/model/game-state";
import BonusResult from "slotworks/model/gameplay/records/results/bonus-result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import SmartUIFlags, { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import { reject } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contracts/contract";

export default class AnimateWinsMediator extends AbstractMediator {
    public static alternateAnimation: boolean = true;

    protected showingBigWin: boolean = false;

    public startAnimating(winnings: number) {
        const winRatio = winnings / betModel.getTotalStake();

        const bigWinLevel = winMagnitude.getBigWinLevel(winRatio);
        const shouldAnimateWinningSymbols = (bigWinLevel >= BigWinThresholds.REGULAR && this.currentRecordHasSymbolWins()) || this.currentRecordHasBonusWin();

        if (bigWinLevel >= BigWinThresholds.SMALL) {
            this.showingBigWin = true;
            bigWinCommand.start(bigWinLevel);
        }

        if (shouldAnimateWinningSymbols) {
            this.animateWinningSymbols();
        }

        SmartUIFlags.set(SmartUIFlag.IDLE, false);

        return super.start();
    }

    public endBigWinAnimation(immediateStop = false) {

        if (GameState.getCurrentGame().isComplete() && !AutoplayModel.isAutoPlaying()) {
            SmartUIFlags.set(SmartUIFlag.IDLE, true);
        }

        if (!this.showingBigWin) {
            return Contract.empty();
        }

        this.showingBigWin = false;

        return new Contract((resolve) => {
            this.stopBigWinAnimation(immediateStop).then(() => {
                resolve(null);
            });
        });
    }

    public end() {

        const symbols = this.getAllWinningSymbols();
        symbols.forEach((symbol) => {
            symbol.static();
            symbol.removeBehaviourOfType(AGSAnimateBehaviour);
            symbol.removeBehaviourOfType(AGSAlternateAnimateBehaviour);
            symbol.addBehaviour(new AGSAnimateBehaviour(symbol));
        });
    }

    public animateWinningSymbols() {
        this.dimNonWinningSymbols();
        this.setSymbolBehaviours();

        const winningSymbols = this.getAllWinningSymbols();
        winningSymbols.forEach((symbol) => {
            symbol.win().execute();
        });
    }

    protected getAllWinningSymbols() {
        const winningSymbols: SymbolComponent[] = [];

        const wins = this.getAllWins();

        wins.forEach((win) => {
            for (const position of win.positions) {
                const symbol = Components.matrix.getSymbol(position.x, position.y);
                winningSymbols.push(symbol);
            }
        });

        return winningSymbols;
    }

    protected currentRecordHasSymbolWins() {
        const symbolWins = GameState.getCurrentGame().getCurrentRecord().getResultsOfType(SymbolWinResult);

        return symbolWins.length > 0;
    }

    protected currentRecordHasBonusWin() {
        const currentRecord = GameState.getCurrentGame().getCurrentRecord();

        return currentRecord.getResultsOfType(BonusResult).length && currentRecord.getResultsOfType(SparkResult).length;
    }

    protected dimNonWinningSymbols() {
        const winningSymbols = this.getAllWinningSymbols();
        const hiddenSymbols = Components.matrix.getHiddenSymbols();
        const remainingSymbols = reject(Components.matrix.getBaseGridSymbols(), winningSymbols);
        DimSymbolsCommand(remainingSymbols.concat(hiddenSymbols), DimType.Cycle).execute();
    }

    private stopBigWinAnimation(immediateStop = false) {
        return bigWinCommand.stop(immediateStop);
    }

    private setSymbolBehaviours() {
        const wins = this.getAllWins();

        let alternateAnimation = AnimateWinsMediator.alternateAnimation;

        wins.forEach((win) => {
            for (const position of win.positions) {
                const symbol = Components.matrix.getSymbol(position.x, position.y);

                symbol.removeBehaviourOfType(AGSAnimateBehaviour);
                symbol.removeBehaviourOfType(AGSAlternateAnimateBehaviour);

                if (alternateAnimation) {
                    symbol.addBehaviour(new AGSAlternateAnimateBehaviour(symbol));
                } else {
                    symbol.addBehaviour(new AGSAnimateBehaviour(symbol));
                }
            }

            if (AnimateWinsMediator.alternateAnimation) {
                alternateAnimation = !alternateAnimation;
            }
        });
    }

    private getAllWins() {
        const record = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;
        const symbolWins = record.getResultsOfType(SymbolWinResult);
        const bonusWins = record.getResultsOfType(BonusResult).length ? record.getResultsOfType(SparkResult) : [];
        return [...symbolWins, ...bonusWins] as SymbolWinResult[];
    }
}
